import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import schedulingApi from '../../api/scheduling';
import usersApi from '../../api/users';
import useAuth from '../../hooks/useAuth';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(iso) {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

const inputCls = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-400';

function Field({ label, children, required }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}{required && ' *'}</label>
      {children}
    </div>
  );
}

// ── SL Assignments sub-section ────────────────────────────────────────────────

function SLAssignments({ semester, slUsers, locations, onFlash }) {
  const queryClient = useQueryClient();
  const [slId,  setSlId]  = useState('');
  const [locId, setLocId] = useState('');

  const { data: assignments = [], isLoading } = useQuery({
    queryKey: ['sl-assignments', semester.id],
    queryFn:  () => schedulingApi.getSlAssignments(semester.id),
  });

  const refetch = () => queryClient.invalidateQueries({ queryKey: ['sl-assignments', semester.id] });

  const addMutation = useMutation({
    mutationFn: () => schedulingApi.createSlAssignment({ sl: slId, location: locId, semester: semester.id }),
    onSuccess:  () => { refetch(); setSlId(''); setLocId(''); onFlash('SL assigned.'); },
    onError:    (err) => onFlash(err.message, 'error'),
  });

  const removeMutation = useMutation({
    mutationFn: schedulingApi.deleteSlAssignment,
    onSuccess:  () => { refetch(); onFlash('Assignment removed.'); },
    onError:    (err) => onFlash(err.message, 'error'),
  });

  // SLs not yet assigned to this semester
  const assignedSlIds = new Set(assignments.map(a => a.sl));
  const availableSLs  = slUsers.filter(u => !assignedSlIds.has(u.id));

  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">SL Location Assignments</p>

      {isLoading ? (
        <p className="text-xs text-gray-400 py-2">Loading…</p>
      ) : assignments.length === 0 ? (
        <p className="text-xs text-gray-400 italic">No SLs assigned yet.</p>
      ) : (
        <div className="space-y-1">
          {assignments.map(a => (
            <div key={a.id} className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
              <span className="text-sm text-gray-800">
                {a.sl_detail ? `${a.sl_detail.first_name} ${a.sl_detail.last_name}` : `SL #${a.sl}`}
              </span>
              <div className="flex items-center gap-3">
                <span className="text-xs font-medium text-indigo-600">
                  {locations.find(l => l.id === a.location)?.name ?? `Location #${a.location}`}
                </span>
                <button onClick={() => removeMutation.mutate(a.id)}
                        className="text-xs text-red-500 hover:text-red-700">Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add assignment row */}
      <div className="flex flex-wrap items-end gap-2 pt-1">
        <div className="flex-1 min-w-[160px]">
          <select className={inputCls} value={slId} onChange={e => setSlId(e.target.value)}>
            <option value="">Select SL…</option>
            {availableSLs.map(u => (
              <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[120px]">
          <select className={inputCls} value={locId} onChange={e => setLocId(e.target.value)}>
            <option value="">Select location…</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>
        <button
          onClick={() => slId && locId && addMutation.mutate()}
          disabled={!slId || !locId || addMutation.isPending}
          className="rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-40">
          Assign
        </button>
      </div>
    </div>
  );
}

// ── Do-Not-Pair sub-section ───────────────────────────────────────────────────

function DoNotPairRules({ semester, slUsers, onFlash }) {
  const queryClient = useQueryClient();
  const [sl1, setSl1] = useState('');
  const [sl2, setSl2] = useState('');

  const { data: rules = [], isLoading } = useQuery({
    queryKey: ['do-not-pair', semester.id],
    queryFn:  () => schedulingApi.getDoNotPair(semester.id),
  });

  const refetch = () => queryClient.invalidateQueries({ queryKey: ['do-not-pair', semester.id] });

  const addMutation = useMutation({
    mutationFn: () => schedulingApi.createDoNotPair({ sl_one: sl1, sl_two: sl2, semester: semester.id }),
    onSuccess:  () => { refetch(); setSl1(''); setSl2(''); onFlash('Do-not-pair rule added.'); },
    onError:    (err) => onFlash(err.message, 'error'),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, is_active }) => schedulingApi.toggleDoNotPair(id, { is_active }),
    onSuccess:  () => { refetch(); onFlash('Rule updated.'); },
    onError:    (err) => onFlash(err.message, 'error'),
  });

  const slName = (id) => {
    const u = slUsers.find(u => u.id === id);
    return u ? `${u.first_name} ${u.last_name}` : `#${id}`;
  };

  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Do-Not-Pair Rules</p>

      {isLoading ? (
        <p className="text-xs text-gray-400 py-2">Loading…</p>
      ) : rules.length === 0 ? (
        <p className="text-xs text-gray-400 italic">No rules set for this semester.</p>
      ) : (
        <div className="space-y-1">
          {rules.map(r => (
            <div key={r.id} className={`flex items-center justify-between rounded-lg px-3 py-2 ${
              r.is_active ? 'bg-red-50' : 'bg-gray-50 opacity-60'
            }`}>
              <span className="text-sm text-gray-800">
                {r.sl_one_detail
                  ? `${r.sl_one_detail.first_name} ${r.sl_one_detail.last_name}`
                  : slName(r.sl_one)}
                {' '}&amp;{' '}
                {r.sl_two_detail
                  ? `${r.sl_two_detail.first_name} ${r.sl_two_detail.last_name}`
                  : slName(r.sl_two)}
              </span>
              <div className="flex items-center gap-3">
                <span className={`text-xs font-medium ${r.is_active ? 'text-red-600' : 'text-gray-400'}`}>
                  {r.is_active ? 'Active' : 'Inactive'}
                </span>
                <button
                  onClick={() => toggleMutation.mutate({ id: r.id, is_active: !r.is_active })}
                  className="text-xs text-indigo-600 hover:text-indigo-800">
                  {r.is_active ? 'Deactivate' : 'Reactivate'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add rule row */}
      <div className="flex flex-wrap items-end gap-2 pt-1">
        <div className="flex-1 min-w-[140px]">
          <select className={inputCls} value={sl1} onChange={e => setSl1(e.target.value)}>
            <option value="">SL one…</option>
            {slUsers.filter(u => u.id !== parseInt(sl2)).map(u => (
              <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[140px]">
          <select className={inputCls} value={sl2} onChange={e => setSl2(e.target.value)}>
            <option value="">SL two…</option>
            {slUsers.filter(u => u.id !== parseInt(sl1)).map(u => (
              <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
            ))}
          </select>
        </div>
        <button
          onClick={() => sl1 && sl2 && addMutation.mutate()}
          disabled={!sl1 || !sl2 || addMutation.isPending}
          className="rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-40">
          Add Rule
        </button>
      </div>
    </div>
  );
}

// ── Semester card ─────────────────────────────────────────────────────────────

function SemesterCard({ semester, slUsers, locations, onFlash }) {
  const [expanded, setExpanded] = useState(false);
  const [tab, setTab] = useState('assignments');
  const queryClient = useQueryClient();

  const toggleMutation = useMutation({
    mutationFn: () => schedulingApi.updateSemester(semester.id, { is_active: !semester.is_active }),
    onSuccess:  () => {
      queryClient.invalidateQueries({ queryKey: ['semesters'] });
      onFlash(semester.is_active ? 'Semester deactivated.' : 'Semester activated.');
    },
    onError: (err) => onFlash(err.message, 'error'),
  });

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors">
        <div className="space-y-0.5">
          <p className="font-semibold text-gray-900">{semester.name}</p>
          <p className="text-xs text-gray-400">
            {fmt(semester.start_date)} — {fmt(semester.end_date)}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
            semester.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
          }`}>
            {semester.is_active ? 'Active' : 'Inactive'}
          </span>
          <svg className={`h-4 w-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
               fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-100">
          {/* Tab bar */}
          <div className="flex items-center gap-1 px-5 pt-3 pb-0">
            {[
              { key: 'assignments', label: 'SL Assignments' },
              { key: 'donotpair',   label: 'Do-Not-Pair' },
            ].map(({ key, label }) => (
              <button key={key} onClick={() => setTab(key)}
                      className={`rounded-t-lg px-4 py-1.5 text-xs font-medium border-b-2 transition-colors ${
                        tab === key
                          ? 'border-indigo-600 text-indigo-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}>
                {label}
              </button>
            ))}
            <div className="ml-auto">
              <button
                onClick={() => toggleMutation.mutate()}
                disabled={toggleMutation.isPending}
                className={`text-xs font-medium ${
                  semester.is_active
                    ? 'text-red-500 hover:text-red-700'
                    : 'text-green-600 hover:text-green-800'
                }`}>
                {semester.is_active ? 'Deactivate semester' : 'Activate semester'}
              </button>
            </div>
          </div>

          <div className="px-5 py-4">
            {tab === 'assignments' && (
              <SLAssignments
                semester={semester}
                slUsers={slUsers}
                locations={locations}
                onFlash={onFlash}
              />
            )}
            {tab === 'donotpair' && (
              <DoNotPairRules
                semester={semester}
                slUsers={slUsers}
                onFlash={onFlash}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Create semester form ───────────────────────────────────────────────────────

function CreateSemesterForm({ currentDistrict, isUpperAdmin, districts, onFlash }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ name: '', start_date: '', end_date: '', district: currentDistrict ?? '' });
  const [open, setOpen] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const mutation = useMutation({
    mutationFn: schedulingApi.createSemester,
    onSuccess:  () => {
      queryClient.invalidateQueries({ queryKey: ['semesters'] });
      setForm({ name: '', start_date: '', end_date: '', district: currentDistrict ?? '' });
      setOpen(false);
      onFlash('Semester created.');
    },
    onError: (err) => onFlash(err.message, 'error'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = { ...form };
    if (!isUpperAdmin) data.district = currentDistrict;
    mutation.mutate(data);
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
              className="rounded-lg border border-dashed border-gray-300 px-4 py-3 text-sm text-gray-500 hover:border-indigo-300 hover:text-indigo-600 w-full text-center transition-colors">
        + Create Semester
      </button>
    );
  }

  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4 space-y-3">
      <p className="text-sm font-semibold text-indigo-800">New Semester</p>
      <form onSubmit={handleSubmit} className="space-y-3">
        <Field label="Name" required>
          <input className={inputCls} value={form.name} onChange={e => set('name', e.target.value)}
                 placeholder="e.g. Fall 2026" required />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Start Date" required>
            <input type="date" className={inputCls} value={form.start_date}
                   onChange={e => set('start_date', e.target.value)} required />
          </Field>
          <Field label="End Date" required>
            <input type="date" className={inputCls} value={form.end_date}
                   onChange={e => set('end_date', e.target.value)} required />
          </Field>
        </div>
        {isUpperAdmin && (
          <Field label="District" required>
            <select className={inputCls} value={form.district} onChange={e => set('district', e.target.value)} required>
              <option value="">Select district…</option>
              {districts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </Field>
        )}
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={() => setOpen(false)}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-white">
            Cancel
          </button>
          <button type="submit" disabled={mutation.isPending}
                  className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-40">
            {mutation.isPending ? 'Creating…' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Main panel ─────────────────────────────────────────────────────────────────

export default function SemestersPanel() {
  const { user: me }  = useAuth();
  const isUpperAdmin  = me?.role === 'UPPER_ADMIN';
  const [toast, setToast] = useState(null);

  const flash = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const { data: semesters = [], isLoading } = useQuery({
    queryKey: ['semesters'],
    queryFn:  schedulingApi.getSemesters,
  });

  const { data: allUsers = [] } = useQuery({
    queryKey: ['users'],
    queryFn:  usersApi.getAll,
  });

  const { data: locations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn:  usersApi.getLocations,
  });

  const { data: districts = [] } = useQuery({
    queryKey: ['districts'],
    queryFn:  usersApi.getDistricts,
    enabled:  isUpperAdmin,
  });

  const slUsers = allUsers.filter(u => u.role === 'SL');

  return (
    <div className="space-y-4">
      {/* Toast */}
      {toast && (
        <div className={`flex items-center justify-between rounded-lg border px-4 py-3 text-sm ${
          toast.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-700'
            : 'bg-red-50 border-red-200 text-red-700'
        }`}>
          {toast.msg}
          <button onClick={() => setToast(null)} className="ml-4 opacity-50 hover:opacity-100">✕</button>
        </div>
      )}

      {/* Semester list */}
      {isLoading ? (
        <p className="text-sm text-gray-400 text-center py-8">Loading…</p>
      ) : (
        <div className="space-y-3">
          {semesters.map(s => (
            <SemesterCard
              key={s.id}
              semester={s}
              slUsers={slUsers}
              locations={locations}
              onFlash={flash}
            />
          ))}
          {semesters.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-6">No semesters yet.</p>
          )}
        </div>
      )}

      <CreateSemesterForm
        currentDistrict={me?.district}
        isUpperAdmin={isUpperAdmin}
        districts={districts}
        onFlash={flash}
      />
    </div>
  );
}
